import _ from 'lodash';
import React, { useMemo, useCallback } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { useGlobalContext, useConfig } from '~/context';
import { log } from '@swiper/core';
import { PersonList } from '~/components';
import { Icon, Button, IdleProgress } from '~/utils';
import { useDebug } from '~/hooks';

// eslint-disable-next-line max-lines-per-function
export default function ReaderPage( props ) {
  const config = useConfig();
  const { session, swipe, dispatch, member } = useGlobalContext();

  log.debug( 'MEMBER', member );

  useDebug( 'session', session );

  const can_bring = useMemo( () => {
    if ( ! member ) return {};
    return _.fromPairs( _.map( [
      'family', 'friend', 'guest',
    ], x => ( [ x, member.flag( `bring_${x}`, `bring_${x}s` ) ] ) ) );
  }, [ member ] );

  const handlePick = useCallback( ( pick ) => {
    log.debug( 'handlePick', pick );
    if ( pick.isOverlay ) {
      dispatch.overlay( pick.id );
      return;
    }
    if ( ( pick.id === swipe.member.id ) || can_bring[ pick.relation ] ) {
      return swipe.toggle( pick );
    }
    // TODO - toast
  }, [ dispatch, swipe, can_bring ] );

  const limits = useMemo( () => {
    if ( ! ( session && config ) ) return {};
    const sim_limit = config.get(
      'settings.guests.simultaneousGuestLimit', 3,
    );
    let day_limit = config.get(
      'settings.guests.dailyGuestLimit', Infinity,
    );
    if ( day_limit <= 0 ) day_limit = Infinity;
    // TODO - Handle guest passes

    const daily_guests = _.uniq( _.map( session.guests, 'guestId' ) ).length;
    const active_guests = _.filter( session.guests, 'present' ).length;
    const active_friends = _.filter( session.friends, 'present' ).length;

    log.debug( 'CANBRING', can_bring );

    const res = {
      daily_guests,
      active_guests,
      active_friends,
      sim_limit,
      day_limit,
    };
    const friends_as_guests = config.get(
      'settings.friends.countTowardGuestLimit', false,
    );
    if ( friends_as_guests ) {
      const total = res.total_guests = active_guests + active_friends;
      res.guests_remain = ( daily_guests < day_limit )
        && ( total < sim_limit );
      res.friends_remain = ( total < sim_limit );
    } else {
      res.guests_remain = ( daily_guests < day_limit )
        && ( active_guests < sim_limit );
      res.friends_remain = true;
    }
    log.debug( 'LIMITS', res );
    return res;
  }, [ can_bring, session, config ] );

  const guests = useMemo( () => {
    if ( ! config.get( 'settings.guests.enabled', false ) ) return false;

    const res = [ ...( session.guests || [] ) ];
    if ( can_bring.guest ) {
      if ( limits.guests_remain ) {
        res.push( {
          id          : 'AddGuestOverlay',
          displayName : <i>Add a Guest</i>,
          isOverlay   : true,
        } );
      } else {
        res.push( {
          // id          : 'GuestLimitOverlay', // TODO
          displayName : <i>Guest Limit Reached</i>,
          isOverlay   : true,
        } );
      }
    }
    return res;
  }, [ config, session, can_bring, limits.guests_remain ] );

  const friends = useMemo( () => {
    if ( ! config.get( 'settings.friends.enabled', false ) ) return false;

    const res = [ ...( session.friends || [] ) ];
    if ( can_bring.friend ) {
      if ( limits.friends_remain ) {
        res.push( {
          id          : 'AddFriendOverlay',
          displayName : <i>Add a Friend</i>,
          isOverlay   : true,
        } );
      } else {
        res.push( {
          // id          : 'GuestLimitOverlay', // TODO
          displayName : <i>Friend Limit Reached</i>,
          isOverlay   : true,
        } );
      }
    }
    return res;
  }, [ config, session, can_bring, limits.friends_remain ] );

  const message = useMemo( () => {
    if ( member.is_staff ) {
      // TODO - If staff offer additional staff options buttons
    } else if ( _.some( can_bring ) ) {
      return (
        <div className="my-5">
          <p>
            To sign someone in or out, press
            the <kbd>letter</kbd> or <kbd>number</kbd> next
            to their name.
          </p>
          <Icon icon="far:user-circle" /> = Signed in
            &nbsp; &nbsp; &nbsp; &nbsp;
          <Icon icon="far:circle" /> = Signed out
        </div>
      );
    } else {
      // return <IconFunny size="9x" className="text-center" />;
    }
    return null;
  }, [ member, can_bring ] );

  return (
    <Container className="actions h-100" fluid>
      <Row>
        <Col>
          <PersonList
            title="Family"
            people={session.members}
            onPick={handlePick}
            selectors="letters"
            data-testid="family-list"
          />
        </Col>
        <Col>
          <PersonList
            title="Guests"
            people={guests}
            onPick={handlePick}
            selectors="numbers"
            data-testid="guests-list"
          />
          <PersonList
            title="Friends"
            people={friends}
            onPick={handlePick}
            selectors="numbers"
            startAt={guests.length}
            data-testid="friends-list"
          />
          {message}
          <Button onClick={dispatch.reset} shortcut="Escape">
            Finished
          </Button>
        </Col>
      </Row>
      <IdleProgress type="swipe" />
    </Container>
  );
}
ReaderPage.propTypes = {
};
